import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { SITE, NAV_LINKS, WA_MESSAGES } from "../data/site";
import { waLink } from "../lib/whatsapp";
import { useGoToSection } from "../lib/useGoToSection";

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const goTo = useGoToSection();

    const go = (id) => {
        setOpen(false);
        goTo(id);
    };

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-brand-brown/10 bg-brand-paper/85 backdrop-blur-xl">
            <nav
                className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16"
                aria-label="Primary"
            >
                <Link to="/" className="flex shrink-0 items-center gap-3" data-testid="nav-logo-link" aria-label="033 Digital — home">
                    <img
                        src={SITE.logoFull}
                        alt="033 Digital logo — Get Found. Get Booked. Get Growing."
                        className="h-12 w-12 rounded-lg object-cover sm:h-14 sm:w-14"
                        width="56"
                        height="56"
                    />
                    <span className="hidden flex-col leading-none sm:flex">
                        <span className="whitespace-nowrap font-display text-lg font-bold tracking-tight text-brand-brown">033 DIGITAL</span>
                        <span className="mt-1 whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.2em] text-brand-orange xl:hidden">
                            Get Found. Get Booked. Get Growing.
                        </span>
                    </span>
                </Link>

                <div className="mx-8 hidden flex-1 items-center justify-center gap-7 xl:flex 2xl:gap-9">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            type="button"
                            onClick={() => go(l.id)}
                            data-testid={`nav-link-${l.id}`}
                            className="whitespace-nowrap text-sm font-medium text-brand-brown/80 transition-colors duration-200 hover:text-brand-orange"
                        >
                            {l.label}
                        </button>
                    ))}
                    <Link
                        to="/blog"
                        data-testid="nav-link-blog"
                        className={`whitespace-nowrap text-sm font-medium transition-colors duration-200 hover:text-brand-orange ${
                            location.pathname.startsWith("/blog") ? "text-brand-orange" : "text-brand-brown/80"
                        }`}
                    >
                        Blog
                    </Link>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                    <a
                        href={waLink(WA_MESSAGES.grow)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-button"
                        className="hidden items-center gap-2 whitespace-nowrap rounded-full border-2 border-brand-brown px-5 py-2.5 text-sm font-semibold text-brand-brown transition-colors duration-200 hover:bg-brand-brown hover:text-white xl:inline-flex"
                    >
                        <MessageCircle size={16} aria-hidden="true" />
                        WhatsApp
                    </a>
                    <button
                        type="button"
                        onClick={() => go("scan")}
                        data-testid="nav-scan-button"
                        className="hidden whitespace-nowrap rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-brand-orange-dark xl:inline-flex"
                    >
                        Business Scan
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        data-testid="nav-mobile-menu-button"
                        aria-expanded={open}
                        aria-label={open ? "Close menu" : "Open menu"}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-brown/15 text-brand-brown xl:hidden"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-brand-brown/10 bg-brand-paper xl:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex flex-col gap-1 px-4 py-6 sm:px-8">
                            {NAV_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    type="button"
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                    onClick={() => go(l.id)}
                                    data-testid={`nav-mobile-link-${l.id}`}
                                    className="rounded-lg px-3 py-3 text-left font-display text-xl font-semibold text-brand-brown transition-colors duration-200 hover:bg-brand-cream hover:text-brand-orange"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.05 * NAV_LINKS.length }}
                            >
                                <Link
                                    to="/blog"
                                    onClick={() => setOpen(false)}
                                    data-testid="nav-mobile-link-blog"
                                    className="block rounded-lg px-3 py-3 text-left font-display text-xl font-semibold text-brand-brown transition-colors duration-200 hover:bg-brand-cream hover:text-brand-orange"
                                >
                                    Blog
                                </Link>
                            </motion.div>
                            <div className="mt-4 flex flex-col gap-3">
                                <button
                                    type="button"
                                    onClick={() => go("scan")}
                                    data-testid="nav-mobile-scan-button"
                                    className="rounded-full bg-brand-orange px-6 py-3.5 text-center text-sm font-semibold text-white"
                                >
                                    Business Scan
                                </button>
                                <a
                                    href={waLink(WA_MESSAGES.grow)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="nav-mobile-whatsapp-button"
                                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-brown px-6 py-3.5 text-sm font-semibold text-brand-brown"
                                >
                                    <MessageCircle size={16} aria-hidden="true" />
                                    WhatsApp Us
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
